<template>
  <div class="liquidaciones-home">
    <div class="header">
      <div class="filters">
        <div class="filter-group">
          <label>Empleado</label>
          <select v-model="filtros.empleado">
            <option value="">Todos</option>
            <option v-for="emp in empleados" :key="emp.id" :value="emp.id">
              {{ emp.nombre }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Estado</label>
          <select v-model="filtros.estado">
            <option value="">Todos</option>
            <option value="borrador">Borrador</option>
            <option value="confirmada">Confirmada</option>
            <option value="anulada">Anulada</option>
          </select>
        </div>

        <button class="btn-primary" @click="abrirModalCrear">
          Nueva Liquidación
        </button>
      </div>
    </div>

    <div v-if="cargando" class="loading">Cargando...</div>

    <table v-else class="liquidaciones-table">
      <thead>
        <tr>
          <th>Número</th>
          <th>Empleado</th>
          <th>Período</th>
          <th>Forma de pago</th>
          <th>Total Horas</th>
          <th>Total</th>
          <th>Estado</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="liq in liquidacionesFiltradas" :key="liq.id">
          <td>{{ liq.numero_formateado }}</td>
          <td>{{ liq.empleado_nombre }}</td>
          <td>{{ formatearFecha(liq.periodo_desde) }} - {{ formatearFecha(liq.periodo_hasta) }}</td>
          <td>{{ liq.periodicidad_display }}</td>
          <td class="numero">{{ liq.horas_total }}</td>
          <td class="numero">{{ formatearMoneda(liq.total) }}</td>
          <td>
            <span :class="['estado-badge', `estado-${liq.estado}`]">
              {{ liq.estado }}
            </span>
          </td>
          <td class="acciones">
            <button class="btn-link" @click="verDetalle(liq.id)">Ver</button>
            <button
              v-if="liq.estado === 'borrador'"
              class="btn-link"
              @click="editarLiquidacion(liq.id)"
            >
              Editar
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Modal para crear nueva liquidación -->
    <div v-if="modalCrearVisible" class="modal-overlay" @click="modalCrearVisible = false">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h2>Nueva Liquidación</h2>
          <button class="btn-close" @click="modalCrearVisible = false">×</button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="guardarNuevaLiquidacion">
            <div class="form-group">
              <label>Empleado *</label>
              <select v-model="formCrear.empleado" required @change="prepararEmpleado()">
                <option value="">Seleccionar empleado</option>
                <option v-for="emp in empleados" :key="emp.id" :value="emp.id">
                  {{ emp.nombre }}
                </option>
              </select>
            </div>

            <div v-if="cargandoPreparar" class="loading">Buscando la condición...</div>

            <!-- Cómo cobra el empleado: lo pactado, que es solo el punto de partida -->
            <div v-if="preparacion && preparacion.condicion" class="condicion-card">
              <div class="condicion-item">
                <span class="etiqueta">Cobra</span>
                <strong>{{ preparacion.condicion.periodicidad_display }}</strong>
              </div>
              <div class="condicion-item">
                <span class="etiqueta">Modalidad</span>
                <strong>{{ preparacion.condicion.modalidad_display }}</strong>
              </div>
              <div class="condicion-item">
                <span class="etiqueta">
                  {{ esPorHora ? 'Valor hora' : 'Importe pactado' }}
                </span>
                <strong>
                  {{ formatearMoneda(esPorHora
                    ? preparacion.condicion.valor_hora_efectivo
                    : preparacion.condicion.importe_fijo) }}
                </strong>
              </div>
              <div v-if="preparacion.ultima_liquidacion" class="condicion-item">
                <span class="etiqueta">Última liquidación</span>
                <strong>hasta {{ formatearFecha(preparacion.ultima_liquidacion.periodo_hasta) }}</strong>
              </div>
            </div>

            <div v-if="preparacion && preparacion.advertencias.length" class="advertencias">
              <p v-for="(aviso, i) in preparacion.advertencias" :key="i">⚠ {{ aviso }}</p>
            </div>

            <template v-if="preparacion && preparacion.condicion">
              <div class="form-group">
                <label>Forma de pago de este período *</label>
                <select v-model="formCrear.periodicidad" @change="cambiarPeriodicidad">
                  <option
                    v-for="opcion in periodicidades"
                    :key="opcion.valor"
                    :value="opcion.valor"
                  >
                    {{ opcion.label }}
                    {{ opcion.valor === preparacion.periodicidad_condicion ? '(la habitual)' : '' }}
                  </option>
                </select>
                <small class="ayuda">
                  Arranca en la periodicidad que tiene asignada. Es una etiqueta y sugiere
                  las fechas: el importe lo deciden los días del período.
                </small>
              </div>

              <div v-if="!esPorHora" class="form-group">
                <label>Cómo se calcula *</label>
                <select v-model="formCrear.base_calculo" @change="refrescarEstimacion">
                  <option v-for="opcion in basesCalculo" :key="opcion.valor" :value="opcion.valor">
                    {{ opcion.label }}
                  </option>
                </select>
                <small class="ayuda">
                  El mes es la unidad: los pagos de adentro del mes son a cuenta y el
                  último cierra la diferencia para completarlo.
                </small>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Período Desde *</label>
                  <input
                    v-model="formCrear.periodo_desde"
                    type="date"
                    required
                    @change="refrescarEstimacion"
                  />
                </div>

                <div class="form-group">
                  <label>Período Hasta *</label>
                  <input
                    v-model="formCrear.periodo_hasta"
                    type="date"
                    required
                    @change="refrescarEstimacion"
                  />
                </div>
              </div>

              <div class="estimacion">
                <div>
                  <span class="etiqueta">Días</span>
                  <strong>{{ preparacion.dias }}</strong>
                </div>
                <div v-if="!esPorHora">
                  <span class="etiqueta">Sueldo del período</span>
                  <strong>{{ formatearMoneda(preparacion.importe_estimado) }}</strong>
                </div>
                <div>
                  <span class="etiqueta">Horas sin liquidar</span>
                  <strong>{{ preparacion.horas_sin_liquidar }}</strong>
                </div>
              </div>

              <!-- Cómo viene el mes al que se imputa este pago -->
              <div v-if="!esPorHora && preparacion.mes" class="control-mes">
                <div class="control-mes-titulo">Mes de {{ preparacion.mes.nombre }}</div>
                <div class="control-mes-filas">
                  <div>
                    <span class="etiqueta">Sueldo del mes</span>
                    <strong>{{ formatearMoneda(preparacion.mes.devengado) }}</strong>
                  </div>
                  <div>
                    <span class="etiqueta">Ya liquidado</span>
                    <strong>{{ formatearMoneda(preparacion.mes.liquidado) }}</strong>
                  </div>
                  <div>
                    <span class="etiqueta">Falta</span>
                    <strong :class="{ 'saldo-negativo': Number(preparacion.mes.saldo) < 0 }">
                      {{ formatearMoneda(preparacion.mes.saldo) }}
                    </strong>
                  </div>
                </div>
                <ul v-if="preparacion.mes.liquidaciones.length" class="control-mes-detalle">
                  <li v-for="liq in preparacion.mes.liquidaciones" :key="liq.id">
                    {{ formatearFecha(liq.periodo_desde) }} –
                    {{ formatearFecha(liq.periodo_hasta) }} ·
                    {{ formatearMoneda(liq.total) }} ({{ liq.estado }})
                  </li>
                </ul>
              </div>
            </template>

            <div class="form-group">
              <label>Observaciones</label>
              <textarea v-model="formCrear.observaciones"></textarea>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn-secondary" @click="modalCrearVisible = false">
                Cancelar
              </button>
              <button type="submit" class="btn-primary" :disabled="cargandoCrear || !puedeCrear">
                {{ cargandoCrear ? 'Creando...' : 'Crear' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal de error -->
    <div v-if="error" class="error-alert">
      <p>{{ error }}</p>
      <button @click="error = null">Cerrar</button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getApiErrorMessage } from '@/util/apiError';
import { formatearFecha as fechaDeCalendario } from '@/util/fecha';
import SueldosAPI, { PERIODICIDADES_PAGO, BASES_CALCULO } from '@/api/sueldos/sueldos';

export default {
  name: 'LiquidacionesHome',
  data() {
    return {
      cargando: false,
      cargandoCrear: false,
      cargandoPreparar: false,
      modalCrearVisible: false,
      error: null,
      empleados: [],
      periodicidades: PERIODICIDADES_PAGO,
      basesCalculo: BASES_CALCULO,
      // Lo que el backend sabe del empleado elegido: condición vigente, período
      // sugerido, importe estimado y avisos de solapamiento.
      preparacion: null,
      filtros: {
        empleado: '',
        estado: '',
      },
      formCrear: {
        empleado: '',
        periodicidad: '',
        periodo_desde: '',
        periodo_hasta: '',
        observaciones: '',
      },
    };
  },
  computed: {
    ...mapGetters('sueldos', ['liquidaciones']),
    esPorHora() {
      return !!this.preparacion
        && !!this.preparacion.condicion
        && this.preparacion.condicion.modalidad === 'HORA';
    },
    puedeCrear() {
      return !!this.preparacion && !!this.preparacion.condicion;
    },
    liquidacionesFiltradas() {
      return this.liquidaciones.filter(liq => {
        if (this.filtros.empleado && liq.empleado !== parseInt(this.filtros.empleado)) {
          return false;
        }
        if (this.filtros.estado && liq.estado !== this.filtros.estado) {
          return false;
        }
        return true;
      });
    },
  },
  methods: {
    ...mapActions('sueldos', [
      'fetchLiquidaciones',
      'crearLiquidacion',
      'prepararLiquidacion',
    ]),
    async cargarLiquidaciones() {
      this.cargando = true;
      try {
        const params = {};
        const response = await this.fetchLiquidaciones(params);
        // El store se actualiza automáticamente via el commit de la mutation
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargando = false;
      }
    },
    async cargarEmpleados() {
      try {
        this.empleados = await SueldosAPI.opciones.empleados();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      }
    },
    abrirModalCrear() {
      this.formCrear = this.formVacio();
      this.preparacion = null;
      this.modalCrearVisible = true;
    },
    formVacio() {
      return {
        empleado: '',
        periodicidad: '',
        base_calculo: 'PRORRATEO',
        periodo_desde: '',
        periodo_hasta: '',
        observaciones: '',
      };
    },
    /**
     * Trae la condición vigente del empleado y con ella propone forma de pago y
     * período. `mantenerFechas` conserva lo que el usuario ya tocó a mano.
     */
    async prepararEmpleado({ mantenerFechas = false } = {}) {
      if (!this.formCrear.empleado) {
        this.preparacion = null;
        return;
      }
      this.cargandoPreparar = true;
      try {
        const params = {
          empleado: this.formCrear.empleado,
          baseCalculo: this.formCrear.base_calculo,
        };
        if (this.formCrear.periodicidad) params.periodicidad = this.formCrear.periodicidad;
        if (mantenerFechas && this.formCrear.periodo_desde && this.formCrear.periodo_hasta) {
          params.desde = this.formCrear.periodo_desde;
          params.hasta = this.formCrear.periodo_hasta;
        }

        const { data } = await this.prepararLiquidacion(params);
        this.preparacion = data;
        if (!data.condicion) return;

        this.formCrear.periodicidad = data.periodicidad;
        this.formCrear.periodo_desde = data.periodo_desde;
        this.formCrear.periodo_hasta = data.periodo_hasta;
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoPreparar = false;
      }
    },
    // Cambiar la forma de pago re-propone el período que le corresponde
    cambiarPeriodicidad() {
      return this.prepararEmpleado();
    },
    // Mover las fechas a mano no re-propone nada: solo actualiza importe y avisos
    refrescarEstimacion() {
      return this.prepararEmpleado({ mantenerFechas: true });
    },
    async guardarNuevaLiquidacion() {
      this.cargandoCrear = true;
      try {
        const data = {
          empleado: parseInt(this.formCrear.empleado),
          periodicidad: this.formCrear.periodicidad,
          base_calculo: this.formCrear.base_calculo,
          periodo_desde: this.formCrear.periodo_desde,
          periodo_hasta: this.formCrear.periodo_hasta,
          observaciones: this.formCrear.observaciones,
        };

        await this.crearLiquidacion(data);
        this.modalCrearVisible = false;
        this.formCrear = this.formVacio();
        this.preparacion = null;
        await this.cargarLiquidaciones();
      } catch (err) {
        this.error = getApiErrorMessage(err);
      } finally {
        this.cargandoCrear = false;
      }
    },
    verDetalle(id) {
      this.$router.push({
        name: 'empresa.sueldos.liquidacion.detalle',
        params: { id },
      });
    },
    editarLiquidacion(id) {
      this.$router.push({
        name: 'empresa.sueldos.liquidacion.detalle',
        params: { id },
      });
    },
    formatearFecha(fecha) {
      return fechaDeCalendario(fecha);
    },
    formatearMoneda(valor) {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
      }).format(valor || 0);
    },
  },
  mounted() {
    this.cargarLiquidaciones();
    this.cargarEmpleados();
  },
};
</script>

<style scoped>
.liquidaciones-home {
  padding: 1rem;
}

.header {
  margin-bottom: 1.5rem;
}

.filters {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #9e9e9e;
}

.filter-group select {
  padding: 0.5rem 0.75rem;
  min-width: 160px;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 6px;
  color: #f5f5f5;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.filter-group select:hover {
  background-color: rgba(255, 255, 255, 0.09);
}

.filter-group select:focus {
  outline: none;
  border-color: #2196f3;
  background-color: rgba(255, 255, 255, 0.09);
}

.filter-group select option {
  background-color: #1a1a1a;
  color: #f5f5f5;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: #9e9e9e;
}

.condicion-card,
.estimacion {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
}

.condicion-card .etiqueta,
.estimacion .etiqueta {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9e9e9e;
  margin-bottom: 0.15rem;
}

.condicion-card strong,
.estimacion strong {
  color: #f5f5f5;
  font-size: 0.95rem;
}

.advertencias {
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid #b58105;
  border-radius: 6px;
  background-color: rgba(181, 129, 5, 0.12);
}

.advertencias p {
  margin: 0 0 0.35rem 0;
  color: #f0c674;
  font-size: 0.875rem;
}

.advertencias p:last-child {
  margin-bottom: 0;
}

.ayuda {
  display: block;
  margin-top: 0.3rem;
  font-size: 0.78rem;
  color: #9e9e9e;
}

.control-mes {
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 6px;
}

.control-mes-titulo {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9e9e9e;
  margin-bottom: 0.6rem;
}

.control-mes-filas {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.control-mes-filas .etiqueta {
  display: block;
  font-size: 0.75rem;
  color: #9e9e9e;
}

.control-mes-filas strong {
  color: #f5f5f5;
}

.saldo-negativo {
  color: #ef5350;
}

.control-mes-detalle {
  margin: 0.7rem 0 0 0;
  padding-left: 1.1rem;
  font-size: 0.8rem;
  color: #9e9e9e;
}

.liquidaciones-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.liquidaciones-table thead {
  background-color: rgba(255, 255, 255, 0.04);
}

.liquidaciones-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #f5f5f5;
  border-bottom: 2px solid rgba(255, 255, 255, 0.16);
}

.liquidaciones-table td {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.liquidaciones-table tr:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.numero {
  text-align: right;
  font-family: 'Courier New', monospace;
}

.estado-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.estado-borrador {
  background-color: #fff3cd;
  color: #856404;
}

.estado-confirmada {
  background-color: #d4edda;
  color: #155724;
}

.estado-anulada {
  background-color: #f8d7da;
  color: #721c24;
}

.acciones {
  text-align: center;
}

.btn-link {
  background: none;
  border: none;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.95rem;
}

.btn-link:hover {
  color: #1565c0;
}

.btn-primary {
  padding: 0.5rem 1rem;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1565c0;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.5rem 1rem;
  background-color: rgba(255, 255, 255, 0.08);
  color: #f5f5f5;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.14);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #1e1e1e;
  color: #f5f5f5;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  max-width: 500px;
  width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #9e9e9e;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.95rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  background-color: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 4px;
  color: #f5f5f5;
  font-size: 0.95rem;
  font-family: inherit;
}

.form-group select option {
  background-color: #1a1a1a;
  color: #f5f5f5;
}

.form-group input[type="date"] {
  color-scheme: dark;
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.modal-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.error-alert {
  padding: 1rem;
  background-color: rgba(244, 67, 54, 0.12);
  border: 1px solid rgba(244, 67, 54, 0.4);
  border-radius: 4px;
  color: #ff8a80;
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.error-alert button {
  background: none;
  border: none;
  color: #ff8a80;
  cursor: pointer;
  text-decoration: underline;
}
</style>
